import React from "react";
import DraggablePanel from "./DraggablePanel";
import { ChevronDown, Clipboard, RefreshCw, Search } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import { useObjectRegistry } from "../../runtime/objectRegistry";
import { getStats as getCacheStats, disposeAll as disposeCache } from "../../runtime/assetCache";
import { useAssetEntries, getAssetActions } from "../../stores/assetStore";
import { clearAllAssets } from "../../utils/idb";
import {
  collectBrowserRuntimeCapabilities,
  getBrowserRuntimeCapabilityMap,
} from "../../utils/browserRuntimeCapabilities";
import { showClipboardUnavailableToast } from "../../utils/browserCapabilityToasts";
import { logger } from "../../utils/logger";

const EMPTY_SCENE_OBJECTS = Object.freeze({});
const RUNTIME_DIAGNOSTICS_TAB_IDS = Object.freeze({
  BROWSER: "browser",
  ASSETS: "assets",
});
const RUNTIME_DIAGNOSTICS_TABS = Object.freeze([
  { id: RUNTIME_DIAGNOSTICS_TAB_IDS.BROWSER, label: "Browser" },
  { id: RUNTIME_DIAGNOSTICS_TAB_IDS.ASSETS, label: "Asset Cache" },
]);
const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between px-3 py-1">
    <span className="text-gray-300 text-sm select-none">{label}</span>
    <span className="text-gray-200 text-sm select-none">{value}</span>
  </div>
);

const StatusBadge = ({ ok, label }) => (
  <span
    className={`rounded border px-2 py-0.5 text-xs ${
      ok
        ? "border-green-800/80 bg-green-950/50 text-green-200"
        : "border-amber-800/80 bg-amber-950/50 text-amber-200"
    }`}
  >
    {label}
  </span>
);

const NeutralBadge = ({ label }) => (
  <span className="rounded border border-gray-700 bg-gray-900/70 px-2 py-0.5 text-xs text-gray-200">
    {label}
  </span>
);

const CapabilityRow = ({ report }) => (
  <div className="px-3 py-1.5">
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-300 text-sm select-none">{report.label}</span>
      <StatusBadge ok={report.ok} label={report.status} />
    </div>
    <div className="mt-0.5 text-xs leading-5 text-gray-400">{report.detail}</div>
  </div>
);

function getMujocoSmokeStatusDisplay(mujocoSmoke) {
  if (mujocoSmoke?.status === "running") return <NeutralBadge label="running" />;
  if (mujocoSmoke?.status === "not-run") return <NeutralBadge label="not run" />;
  if (mujocoSmoke?.ok === true) return <StatusBadge ok label="passed" />;
  if (mujocoSmoke?.status === "skipped") return <StatusBadge ok={false} label="skipped" />;
  return <StatusBadge ok={false} label="failed" />;
}

const SectionTitle = ({ children }) => (
  <h3 className="px-3 pt-2 text-xs font-semibold uppercase text-gray-300">{children}</h3>
);

const RuntimeDiagnosticsTabStrip = React.memo(function RuntimeDiagnosticsTabStrip({
  activeTabId,
  onTabChange,
}) {
  return (
    <div className="px-3 pt-2">
      <div className="flex flex-wrap gap-0.5 pb-px" role="tablist" aria-label="Runtime diagnostics">
        {RUNTIME_DIAGNOSTICS_TABS.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`shrink-0 rounded-t border px-2.5 py-1.5 text-xs transition-colors select-none ${
                isActive
                  ? "border-gray-600 border-b-gray-800 bg-gray-800 text-gray-100 -mb-px z-[1]"
                  : "border-transparent text-gray-300 hover:text-gray-100 hover:bg-gray-800/40"
              }`}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
});

function collectRuntimeReport(headerReport = {}, mujocoSmoke = {}) {
  const capabilities = collectBrowserRuntimeCapabilities();
  const capabilityMap = getBrowserRuntimeCapabilityMap(capabilities);
  return {
    generatedAt: new Date().toISOString(),
    pageUrl: globalThis.location?.href || "",
    capabilities,
    runtime: {
      userAgent: globalThis.navigator?.userAgent || "",
      platform: globalThis.navigator?.platform || "",
      secureContext: globalThis.isSecureContext === true,
      crossOriginIsolated: globalThis.crossOriginIsolated === true,
      modernJs: capabilityMap.modernJs,
      webgl2: capabilityMap.webgl2,
      webAssembly: typeof WebAssembly !== "undefined",
      sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
      resizeObserver: capabilityMap.resizeObserver,
      intersectionObserver: capabilityMap.intersectionObserver,
      wasmSimd: capabilityMap.wasmSimd,
      moduleWorker: typeof Worker !== "undefined",
      indexedDB: capabilityMap.indexedDB,
      webCrypto: capabilityMap.webCrypto,
      clipboard: capabilityMap.clipboard,
      physicsIsolation: capabilityMap.physicsIsolation,
    },
    headers: headerReport,
    mujocoSmoke,
  };
}

async function readResponseHeaders(path) {
  try {
    const response = await fetch(path, {
      cache: "no-store",
      credentials: "same-origin",
    });
    return {
      ok: response.ok,
      status: response.status,
      coop: response.headers.get("Cross-Origin-Opener-Policy") || "",
      coep: response.headers.get("Cross-Origin-Embedder-Policy") || "",
      corp: response.headers.get("Cross-Origin-Resource-Policy") || "",
      contentType: response.headers.get("Content-Type") || "",
    };
  } catch (error) {
    return {
      ok: false,
      status: "fetch-error",
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

function createMujocoSmokeWorkerUrl() {
  const mujocoModuleUrl = new URL("/mujoco3/mujoco.js", globalThis.location.origin).href;
  const mujocoBaseUrl = new URL("/mujoco3/", globalThis.location.origin).href;
  const source = `
    const startedAt = Date.now();
    const logs = [];
    const post = (payload) => self.postMessage(payload);
    try {
      const loadMujoco = (await import(${JSON.stringify(mujocoModuleUrl)})).default;
      const module = await loadMujoco({
        locateFile: (path) => new URL(path, ${JSON.stringify(mujocoBaseUrl)}).href,
        print: (message) => logs.push({ level: "info", message: String(message) }),
        printErr: (message) => logs.push({ level: "error", message: String(message) }),
      });
      post({
        ok: true,
        status: "complete",
        durationMs: Date.now() - startedAt,
        moduleReady: !!module,
        logs: logs.slice(-12),
      });
    } catch (error) {
      post({
        ok: false,
        status: "failed",
        durationMs: Date.now() - startedAt,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : "",
        logs: logs.slice(-12),
      });
    }
  `;
  const blob = new Blob([source], { type: "text/javascript" });
  return URL.createObjectURL(blob);
}

const AssetStatsPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const sceneObjects = useObjectRegistry() ?? EMPTY_SCENE_OBJECTS;
  const entryMap = useAssetEntries();
  // Blob URL revocation is handled by asset cache disposal now

  const [stats, setStats] = React.useState(() => getCacheStats());
  const [showRegistryKeys, setShowRegistryKeys] = React.useState(false);
  const [filter, setFilter] = React.useState("");
  const [headerReport, setHeaderReport] = React.useState({});
  const [mujocoSmoke, setMujocoSmoke] = React.useState({ status: "not-run" });
  const [copyState, setCopyState] = React.useState("idle");
  const [activeTabId, setActiveTabId] = React.useState(RUNTIME_DIAGNOSTICS_TAB_IDS.BROWSER);

  const runtimeReport = React.useMemo(
    () => collectRuntimeReport(headerReport, mujocoSmoke),
    [headerReport, mujocoSmoke]
  );

  const refreshHeaders = React.useCallback(async () => {
    const next = {
      document: await readResponseHeaders(globalThis.location?.pathname || "/"),
      mujocoJs: await readResponseHeaders("/mujoco3/mujoco.js"),
      mujocoWasm: await readResponseHeaders("/mujoco3/mujoco.wasm"),
    };
    setHeaderReport(next);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    void refreshHeaders();
  }, [open, refreshHeaders]);

  const runMujocoSmoke = React.useCallback(async () => {
    if (globalThis.crossOriginIsolated !== true || typeof SharedArrayBuffer === "undefined") {
      setMujocoSmoke({
        status: "skipped",
        ok: false,
        error: "SharedArrayBuffer is unavailable because this page is not cross-origin isolated.",
      });
      return;
    }

    setMujocoSmoke({ status: "running" });
    const workerUrl = createMujocoSmokeWorkerUrl();
    const worker = new Worker(workerUrl, { type: "module", name: "critter-mujoco-smoke" });

    try {
      const result = await new Promise((resolve) => {
        const timeout = window.setTimeout(() => {
          resolve({
            ok: false,
            status: "timeout",
            error: "MuJoCo module smoke timed out after 15000 ms.",
          });
        }, 15000);

        worker.onmessage = (event) => {
          window.clearTimeout(timeout);
          resolve(event.data);
        };
        worker.onerror = (event) => {
          window.clearTimeout(timeout);
          resolve({
            ok: false,
            status: "worker-error",
            error: event.message || "Worker error",
          });
        };
      });

      setMujocoSmoke(result);
    } finally {
      worker.terminate();
      URL.revokeObjectURL(workerUrl);
    }
  }, []);

  const copyReport = React.useCallback(async () => {
    try {
      await globalThis.navigator?.clipboard?.writeText(JSON.stringify(runtimeReport, null, 2));
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1200);
    } catch (error) {
      logger.warn("[AssetStatsPanel] Failed to copy runtime diagnostics report", error);
      showClipboardUnavailableToast();
      setCopyState("error");
    }
  }, [runtimeReport]);

  // Periodically refresh cache stats while panel is open
  React.useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      try {
        setStats(getCacheStats());
      } catch (error) {
        logger.warn("[AssetStatsPanel] Failed to refresh cache stats", error);
      }
    }, 800);
    return () => clearInterval(id);
  }, [open]);

  const registryCount = Object.keys(sceneObjects || {}).length;
  const assetCounts = React.useMemo(() => {
    let meshes = 0,
      textures = 0,
      total = 0,
      idle = 0,
      loading = 0,
      ready = 0,
      error = 0;
    Object.values(entryMap || {}).forEach((e) => {
      if (!e) return;
      total += 1;
      if (e.kind === "mesh") meshes += 1;
      if (e.kind === "texture") textures += 1;
      switch (e.status) {
        case "idle":
          idle += 1;
          break;
        case "loading":
          loading += 1;
          break;
        case "ready":
          ready += 1;
          break;
        case "error":
          error += 1;
          break;
        default:
          break;
      }
    });
    return { total, meshes, textures, idle, loading, ready, error };
  }, [entryMap]);

  const allKeys = React.useMemo(() => Object.keys(sceneObjects || {}).sort(), [sceneObjects]);
  const keys = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return allKeys;
    return allKeys.filter((k) => k.toLowerCase().includes(q));
  }, [allKeys, filter]);

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Runtime Diagnostics"
      titleIcon={titleIcon}
      initialSize={{ width: 480, height: 620 }}
      minWidth={300}
      minHeight={260}
      mobilePinPanelId={mobilePinPanelId}
    >
      <RuntimeDiagnosticsTabStrip activeTabId={activeTabId} onTabChange={setActiveTabId} />
      <div className="flex-1 overflow-auto border-t border-gray-600/70 p-3 space-y-2">
        {activeTabId === RUNTIME_DIAGNOSTICS_TAB_IDS.BROWSER ? (
          <>
            <SectionTitle>Browser Runtime</SectionTitle>
            {runtimeReport.capabilities.map((report) => (
              <CapabilityRow key={report.id} report={report} />
            ))}
            <div className="h-px bg-gray-700 my-2" />
            <SectionTitle>Session Details</SectionTitle>
            <StatRow label="Secure context" value={String(runtimeReport.runtime.secureContext)} />
            <StatRow
              label="Cross-origin isolated"
              value={
                <StatusBadge
                  ok={runtimeReport.runtime.crossOriginIsolated}
                  label={runtimeReport.runtime.crossOriginIsolated ? "enabled" : "disabled"}
                />
              }
            />
            <StatRow
              label="SharedArrayBuffer"
              value={
                <StatusBadge
                  ok={runtimeReport.runtime.sharedArrayBuffer}
                  label={runtimeReport.runtime.sharedArrayBuffer ? "available" : "missing"}
                />
              }
            />
            <StatRow label="WebAssembly" value={String(runtimeReport.runtime.webAssembly)} />
            <StatRow label="Module worker" value={String(runtimeReport.runtime.moduleWorker)} />

            <div className="flex flex-wrap items-center gap-2 px-3 py-2">
              <button
                className="inline-flex items-center gap-1.5 rounded border border-gray-600 px-3 py-1 text-gray-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={runMujocoSmoke}
                disabled={mujocoSmoke.status === "running"}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {mujocoSmoke.status === "running" ? "Running..." : "Run MuJoCo Smoke"}
              </button>
              <button
                className="inline-flex items-center gap-1.5 rounded border border-gray-600 px-3 py-1 text-gray-200 hover:bg-gray-700"
                onClick={copyReport}
              >
                <Clipboard className="h-3.5 w-3.5" />
                {copyState === "copied"
                  ? "Copied"
                  : copyState === "error"
                    ? "Copy Failed"
                    : "Copy Report"}
              </button>
            </div>

            <StatRow label="MuJoCo smoke" value={getMujocoSmokeStatusDisplay(mujocoSmoke)} />
            {mujocoSmoke.ok === true && (
              <>
                <StatRow label="Smoke duration" value={`${mujocoSmoke.durationMs || 0} ms`} />
                <StatRow
                  label="Module ready"
                  value={
                    <StatusBadge
                      ok={mujocoSmoke.moduleReady === true}
                      label={mujocoSmoke.moduleReady === true ? "yes" : "no"}
                    />
                  }
                />
              </>
            )}
            {mujocoSmoke.error && (
              <div className="mx-3 rounded border border-amber-800/80 bg-amber-950/40 px-3 py-2 text-xs leading-5 text-amber-100">
                {mujocoSmoke.error}
              </div>
            )}

            <div className="h-px bg-gray-700 my-2" />
            <SectionTitle>Response Headers</SectionTitle>
            <StatRow label="Document COOP" value={headerReport.document?.coop || "(missing)"} />
            <StatRow label="Document COEP" value={headerReport.document?.coep || "(missing)"} />
            <StatRow label="MuJoCo JS CORP" value={headerReport.mujocoJs?.corp || "(missing)"} />
            <StatRow
              label="MuJoCo WASM CORP"
              value={headerReport.mujocoWasm?.corp || "(missing)"}
            />
          </>
        ) : (
          <>
            <SectionTitle>Asset Cache</SectionTitle>
            <StatRow label="Registry" value={registryCount} />
            <StatRow
              label="Assets"
              value={`${assetCounts.total} (M ${assetCounts.meshes}, T ${assetCounts.textures})`}
            />
            <StatRow
              label="Cache"
              value={`${stats.cacheSize} refs ${stats.refsSize} inflight ${stats.inflightSize}`}
            />
            {typeof stats.urlsTracked === "number" && (
              <StatRow label="Object URLs" value={`${stats.urlsTracked}`} />
            )}
            <StatRow
              label="Statuses"
              value={`idle ${assetCounts.idle} · loading ${assetCounts.loading} · ready ${assetCounts.ready} · error ${assetCounts.error}`}
            />

            <div className="h-px bg-gray-700 my-2" />

            <div className="flex items-center gap-2 px-3">
              <button
                className="px-3 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
                onClick={() => {
                  try {
                    disposeCache();
                  } catch (error) {
                    logger.warn("[AssetStatsPanel] Failed to dispose cache", error);
                  }
                  try {
                    setStats(getCacheStats());
                  } catch (error) {
                    logger.warn(
                      "[AssetStatsPanel] Failed to refresh cache stats after dispose",
                      error
                    );
                  }
                }}
              >
                Dispose Cache
              </button>
              {/* Revoke Blob URLs is folded into Dispose Cache via assetCache */}
              <button
                className="px-3 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
                onClick={() => {
                  try {
                    getAssetActions().rehydrateFromPersisted();
                  } catch (error) {
                    logger.warn("[AssetStatsPanel] Failed to reload manifest", error);
                  }
                }}
              >
                Reload Manifest
              </button>
              <Tooltip content="Delete all persisted assets and manifest">
                <button
                  className="px-3 py-1 rounded border border-red-700 text-red-200 hover:bg-red-800/40"
                  aria-label="Delete all persisted assets and manifest"
                  onClick={async () => {
                    const ok = window.confirm(
                      "Delete all persisted assets? This will clear the asset manifest and all stored bytes."
                    );
                    if (!ok) return;
                    try {
                      // Clear runtime cache and blob URLs first
                      try {
                        disposeCache();
                      } catch (error) {
                        logger.warn(
                          "[AssetStatsPanel] Failed to dispose cache while deleting all assets",
                          error
                        );
                      }
                      // Clear IDB stores
                      await clearAllAssets();
                      // Reset asset store state
                      getAssetActions().clearAll();
                      // Refresh panel numbers
                      try {
                        setStats(getCacheStats());
                      } catch (error) {
                        logger.warn(
                          "[AssetStatsPanel] Failed to refresh stats after deleting assets",
                          error
                        );
                      }
                    } catch (e) {
                      logger.warn("[AssetStatsPanel] Failed to clear all assets", e);
                    }
                  }}
                >
                  Delete All Assets
                </button>
              </Tooltip>
            </div>

            <div className="h-px bg-gray-700 my-2" />

            {/* Registry Keys collapsible */}
            <div className="px-2">
              <div
                role="button"
                aria-expanded={showRegistryKeys}
                className="flex items-center justify-between px-2 py-2 rounded-md hover:bg-gray-800 cursor-pointer select-none"
                onClick={() => setShowRegistryKeys((v) => !v)}
              >
                <div className="flex items-center gap-2">
                  <ChevronDown
                    className="w-4 h-4 text-gray-300 transition-transform"
                    style={{ transform: showRegistryKeys ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                  <span className="text-gray-200 text-sm">Registry Keys</span>
                  <span className="text-xs text-gray-400">({allKeys.length})</span>
                </div>
              </div>

              {showRegistryKeys && (
                <div className="px-1 pb-2">
                  <div className="flex items-center gap-2 px-2">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        aria-label="Filter registry keys"
                        placeholder="Filter keys..."
                        className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-gray-600 bg-gray-800 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
                      />
                    </div>
                    {filter && (
                      <button
                        className="text-xs text-gray-300 hover:text-white px-2 py-1 border border-gray-600 rounded"
                        onClick={() => setFilter("")}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="mt-2 px-2 py-2 text-xs text-gray-200 space-y-1 border-t border-gray-700">
                    {keys.length === 0 ? (
                      <div className="text-gray-400">(no matches)</div>
                    ) : (
                      keys.map((k) => (
                        <div key={k} className="text-gray-300 break-words">
                          {k}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DraggablePanel>
  );
};

export default AssetStatsPanel;
